<template>
  <div>
    <div v-for="(item, index) in itemsToDragDrop" :key="item.id" 
         :draggable="true" 
         @dragstart="onDragStart(index)"
         @dragover.prevent="onDragOver(index)" 
         @drop="onDrop(index)" 
         @dragend="onDragEnd"
         @touchstart="onTouchStart($event, index)" 
         @touchmove="onTouchMove($event, index)"
         @touchend="onTouchEnd($event, index)"
         :class="{'highlight-drop-zone': index === dropIndex}">
      <div>
        <slot :item="item" :index="index" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DragAndDropComponent',
  props: {
    items: {
      type: Array,
      default: () => [],
      required: false
    },
    onDragDrop: {
      type: Function,
      default: () => {},
      required: false
    },
  },
  data() {
    return {
      draggedItemIndex: null,
      itemsToDragDrop: [],

      // Índice donde se mostrará la vista previa del drop
      dropIndex: null,  
      touchStartY: 0,
      dragThreshold: 10,

      // Umbral para diferenciar clic de arrastre
      timeThreshold: 200
    };
  },
  watch: {
    items: {
      immediate: true, 
      handler(newItems) {
        this.itemsToDragDrop = [...newItems];
      }
    }
  },
  methods: {

    onDragStart(index) {
      this.draggedItemIndex = index;
    },

    /**
     * Actualiza el índice de la vista previa.
     * 
     * @param index 
     */
    onDragOver(index) {
      this.dropIndex = index; 
    },

    /**
     * Evento Drag-drop.
     * 
     * @param index 
     */
    async onDrop(index) {
      this.handleDrop(index);

      // Reiniciar la vista previa
      this.dropIndex = null;
    },

    /**
     * Asegúrate de quitar la vista previa al final del drag.
     */
    onDragEnd() {
      this.dropIndex = null; 
    },

    /**
     * Captura la posición y tiempo inicial del toque.
     * 
     * @param event 
     * @param index 
     */
    onTouchStart(event, index) {
      this.touchStartY = event.touches[0].clientY;
      this.touchStartTime = new Date().getTime();
      this.draggedItemIndex = index;
    },

    /**
     * Prevenir el comportamiento predeterminado del desplazamiento (scroll) durante el gesto de arrastre.
     * 
     * @param event 
     * @param index 
     */
    onTouchMove(event, index) {
      event.preventDefault();

      // Actualiza el índice durante el movimiento del toque
      this.dropIndex = index;  
    },

    /**
     * Al soltar el toque (touchend), se calcula el índice donde se soltó el elemento.
     * 
     * @param event 
     * @param originalIndex 
     */
    onTouchEnd(event, originalIndex) {
      const touchEndY = event.changedTouches[0].clientY;
      const deltaY = Math.abs(touchEndY - this.touchStartY);
      const touchEndTime = new Date().getTime();
      const deltaTime = touchEndTime - this.touchStartTime;

      // Diferenciar entre clic y arrastre
      if (deltaY <= this.dragThreshold && deltaTime <= this.timeThreshold) return;

      const dropIndex = this.calculateDropIndex(touchEndY, originalIndex);
      this.handleDrop(dropIndex);

      // Reiniciar la vista previa al soltar
      this.dropIndex = null;  
    },

    /**
     * Mueve el elemento arrastrado a la nueva posición.
     * 
     * @param dropIndex 
     */
    handleDrop(dropIndex) {
      const draggedItem = this.itemsToDragDrop.splice(this.draggedItemIndex, 1)[0];
      this.itemsToDragDrop.splice(dropIndex, 0, draggedItem);
      this.draggedItemIndex = null;

      this.processEventAfterMove(dropIndex, draggedItem, true);
    },

    /**
     * Calcula el índice de soltar basado en la posición Y final del toque.
     * 
     * @param touchEndY 
     * @param originalIndex 
     * @returns dropIndex
     */
     calculateDropIndex(touchEndY, originalIndex) {
      let dropIndex = originalIndex;

      for (let i = 0; i < this.itemsToDragDrop.length; i++) {
        const itemElement = this.$el.children[i];
        const itemRect = itemElement.getBoundingClientRect();

        if (touchEndY >= itemRect.top && touchEndY <= itemRect.bottom) {
          dropIndex = i;
          break;
        }
      }

      return dropIndex;
    },

    /**
     * Procesar solicitud cuando se haga un movimiento.
     * 
     * @param dropIndex 
     * @param draggedItem 
     * @param useConditionalItemsEquals 
     */
    async processEventAfterMove(dropIndex, draggedItem, useConditionalItemsEquals = true) {
      const newIndex = this.itemsToDragDrop.findIndex(x => x.id === draggedItem.id);
      await this.onDragDrop(this.itemsToDragDrop, dropIndex, newIndex + 1, useConditionalItemsEquals);
    }
  }
};
</script>

<style scoped>

/* Clase CSS para resaltar la zona de drop */
.highlight-drop-zone {
  border: 2px dashed #63c2de;
  background-color: rgba(0, 0, 255, 0.1);
  opacity: 0.3;
}

</style>
